import React, { Suspense } from 'react';
import PropTypes from 'prop-types';

import { toast, AlertDialog } from '@meronex/components';

import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';

import { useTheme } from '@material-ui/core/styles';
import MdClose from '@meronex/icons/md/MdClose';
import StoreItemDetails from '../StoreItemDetails';
import MenuList from '@material-ui/core/MenuList';
import IDelete from '@material-ui/icons/Delete';
import MenuItem from '@material-ui/core/MenuItem';

import IOut from '@meronex/icons/bi/BiExport';
import IIn from '@meronex/icons/bi/BiImport';
import IManage from '@meronex/icons/ri/RiListSettingsLine';
import IRestore from '@meronex/icons/mdc/MdcFileRestoreOutline';

import ILocation from '@meronex/icons/en/EnLocation';
import IArchive from '@meronex/icons/bs/BsArchive';
import IEdit from '@meronex/icons/ai/AiTwotoneEdit';
import IQRCode from '@meronex/icons/mdc/MdcQrcodeScan';
import IBox from '@meronex/icons/fi/FiBox';
import IAlert from '@meronex/icons/fi/FiAlertTriangle';
import { Icons } from '../../../../common/Icons';

import { storesMng } from '../../../api/storesMng';
import { currentRole } from '../../../../common/roles';
import { supportContactLine } from '../../../../../config/support';

import ModifyDialog from './ModifyDialog';
import LocationDialog from './LocationDialog';
import AdjustUnitRateDialog from './AdjustUnitRateDialog/AdjustUnitRateDialog';
import EditItemTypeDialog from '../../itemTypes/EditItemTypeDialog';
const LabelDialog = React.lazy(() => import('../labels/LabelDialog'));

function ActionDialog(props) {
  const theme = useTheme();
  const isDark = theme.palette.type === 'dark';

  const {
    open,
    onClose,
    item,
    permissions,
    itemProducts,
    onItemIn,
    onItemOut,
    onItemTransfer,
    onItemManage,
    onItemDelete,
    onItemRestore,
    onToggleActive,
    onModifyStoreItem,
    onArchiveStoreItem,
    onAdjustUnitRate,
  } = props;

  const [clickCount, setClickCount] = React.useState(0);
  const [itemState, setItemState] = React.useState(item);

  const [openLabelDialog, setOpenLabelDialog] = React.useState(false);
  const [openLocationDialog, setOpenLocationDialog] = React.useState(false);
  const [openSectionsHelp, setOpenSectionsHelp] = React.useState(false);
  const [openAdjustUnitRateDialog, setOpenAdjustUnitRateDialog] = React.useState(false);
  const [openModifyDialog, setOpenModifyDialog] = React.useState(false);
  const [openEditItemTypeDialog, setOpenEditItemTypeDialog] = React.useState(false);
  const [openEditTypeConfirm, setOpenEditTypeConfirm] = React.useState(false);
  const [openArchiveConfirm, setOpenArchiveConfirm] = React.useState(false);
  const [openReorderDialog, setOpenReorderDialog] = React.useState(false);
  const [reorderValue, setReorderValue] = React.useState('');

  React.useEffect(() => {
    setItemState(item);
  }, [item]);

  if (!itemState) {
    return null;
  }

  const _onClose = () => {
    onClose();
  };

  const _onItemDelete = async () => {
    const result = await storesMng.deleteStoreItem(itemState._id);
    toast.success('Store item deleted', { autoClose: 800 });
    onItemDelete();
    _onClose();
    onClose();
  };
  const _onItemRestore = async () => {
    const result = await storesMng.restoreStoreItem(itemState._id);
    toast.success('Store item restored', { autoClose: 800 });
    _onClose();
    onItemRestore();
    onClose();
  };

  const _onToggleActive = async (itemId, isActive) => {
    try {
      await storesMng.updateStoreItem(itemId, {
        _id: itemState._id,
        name: itemState.type.name,
        itemCount: itemState.quantity,
        communityId: itemState.communityId,
        createdAt: itemState.createdAt,
        isActive,
      });
      // Update local state immediately
      setItemState((prev) => ({ ...prev, isActive }));
      toast.success(
        `Item ${isActive ? 'activated' : 'deactivated'} successfully`,
        { autoClose: 800 }
      );
      if (onToggleActive) {
        onToggleActive();
      }
    } catch (error) {
      console.error('Error toggling item active state:', error);
      toast.error('Failed to update item status');
    }
  };

  const renderButton = ({ onClick, label, icon, color, disabled }) => {
    const isInactive = !itemState.archived && itemState.isActive === false;
    const buttonDisabled = disabled || isInactive;

    return (
      <MenuItem
        onClick={() => {
          if (!buttonDisabled && typeof onClick === 'function') {
            onClick();
          }
        }}
        style={{
          width: '100%',
          color: isInactive ? (isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0, 0, 0, 0.38)') : color,
          minHeight: '75px',
          borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : '#e8e7e8'}`,
          backgroundColor: isDark ? 'transparent' : undefined,
          opacity: isInactive ? 0.6 : 1,
          cursor: buttonDisabled ? 'not-allowed' : 'pointer',
          pointerEvents: buttonDisabled ? 'none' : 'auto',
        }}>
        <div
          style={{
            width: '100%',
            textAlign: 'left',
          }}>
          {icon}

          <span
            style={{
              marginLeft: '15px',
              fontSize: '18px',
            }}>
            {label}
          </span>
        </div>
      </MenuItem>
    );
  };

  const renderManageButton = ({ onClick, label, icon, disabled, secondary, secondaryMuted, danger, title, mutedIcon }) => {
    const iconColor = danger
      ? (isDark ? '#f87171' : '#dc2626')
      : mutedIcon
        ? (isDark ? '#475569' : '#cbd5e1')
        : (isDark ? '#94a3b8' : '#64748b');
    return (
      <div
        title={title}
        onClick={() => {
          if (!disabled) onClick();
        }}
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '14px 8px',
          borderRadius: '10px',
          border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : '#e8e7e8'}`,
          backgroundColor: isDark ? 'rgba(255,255,255,0.02)' : '#fafafa',
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.4 : 1,
          transition: 'background-color 0.15s',
          minHeight: '72px',
        }}
        onMouseEnter={(e) => {
          if (!disabled) e.currentTarget.style.backgroundColor = isDark ? 'rgba(255,255,255,0.06)' : '#f1f5f9';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = isDark ? 'rgba(255,255,255,0.02)' : '#fafafa';
        }}>
        <span style={{ color: iconColor, display: 'flex', alignItems: 'center', marginBottom: '6px' }}>
          {icon}
        </span>
        <span style={{
          fontSize: '12px',
          fontWeight: 500,
          color: danger ? iconColor : (isDark ? '#cbd5e1' : '#334155'),
          textAlign: 'center',
          lineHeight: '1.3',
        }}>
          {label}
        </span>
        {secondary && (
          <span style={{ fontSize: '10px', color: secondaryMuted ? (isDark ? '#475569' : '#cbd5e1') : (isDark ? '#818cf8' : '#6366f1'), marginTop: '2px' }}>
            {secondary}
          </span>
        )}
      </div>
    );
  };

  const _onArchiveItem = async () => {
    try {
      await storesMng.archiveStoreItem(itemState._id);
      toast.success('Store item archived', { autoClose: 900 });
      if (onArchiveStoreItem) {
        onArchiveStoreItem();
      }
      _onClose();
    } catch (error) {
      console.error('Error archiving item:', error);
      toast.error('Failed to archive item');
    }
  };

  const handleReorderSave = async () => {
    const val = parseInt(reorderValue, 10);
    if (isNaN(val) || val < 0) {
      setOpenReorderDialog(false);
      return;
    }
    try {
      await storesMng.updateStoreItemThreshold({
        storeItemId: itemState._id,
        lowStockThreshold: val,
      });
      setItemState((prev) => ({ ...prev, lowStockThreshold: val }));
      toast.success(val > 0 ? `Reorder level set to ${val}` : 'Reorder level cleared', { autoClose: 800 });
    } catch (e) {
      console.error('Failed to update reorder level:', e);
      toast.error('Failed to update reorder level');
    }
    setOpenReorderDialog(false);
  };

  const disableArchiving = Array.isArray(itemProducts) && itemProducts.length > 0;

  return (
    <Dialog
      disableEscapeKeyDown={true}
      disableBackdropClick={true}
      onBackdropClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
      }}
      open={open}
      scroll={'body'}
      className={'fullScreenMobile noselect'}>
      <DialogTitle style={{ paddingBottom: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <div
            onClick={_onClose}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '36px',
              height: '36px',
              borderRadius: '8px',
              cursor: 'pointer',
              color: isDark ? '#94a3b8' : '#64748b',
              fontSize: '20px',
              transition: 'background-color 0.15s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = isDark ? 'rgba(255,255,255,0.06)' : '#f1f5f9'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}>
            <MdClose />
          </div>
        </div>
      </DialogTitle>
      <DialogContent>
        <div
          onClick={() => {
            setClickCount(clickCount + 1);
          }}
          style={{
            marginTop: '-10px',
            marginBottom: '20px',
          }}>
          <StoreItemDetails
            item={itemState}
            permissions={permissions}
            prodcuts={itemProducts}
            showTransaction={true}
            showStore={true}
            showUpdated
            showId={clickCount > 2}
            showDetails={true}
            showBatches={true}
            onToggleActive={_onToggleActive}
          />
        </div>
        {itemState.archived ? (
          /* Archived items: only History, Restore, Delete */
          <MenuList
            style={{
              padding: '0px',
              marginTop: '10px',
              marginBottom: '35px',
              border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : '#e8e7e8'}`,
              borderRadius: '10px',
              backgroundColor: isDark ? 'transparent' : undefined,
            }}>
            {permissions.auditItem &&
              renderButton({
                onClick: onItemManage,
                label: 'History',
                icon: <IManage size={25} />,
                color: isDark ? '#e2e8f0' : 'black',
              })}
            {permissions.restore &&
              renderButton({
                onClick: () => {
                  _onItemRestore();
                },
                label: 'Restore',
                icon: <IRestore size={25} />,
                color: '#16a34a',
              })}
            {permissions.delete &&
              renderButton({
                onClick: () => {
                  _onItemDelete();
                },
                label: 'Delete',
                icon: <IDelete size={25} />,
                color: isDark ? '#f87171' : '#dc2626',
              })}
          </MenuList>
        ) : (
          /* Active items: full action menu */
          <MenuList
            style={{
              padding: '0px',
              marginTop: '10px',
              marginBottom: '35px',
              border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : '#e8e7e8'}`,
              borderRadius: '10px',
              backgroundColor: isDark ? 'transparent' : undefined,
            }}>
            {permissions.in &&
              !itemState.store.productsStore &&
              renderButton({
                onClick: onItemIn,
                label: 'In',
                icon: <IIn size={25} />,
                color: '#16a34a',
                disabled: !permissions.in || itemState.isActive === false,
              })}
            {permissions.out &&
              renderButton({
                onClick: onItemOut,
                label: 'Out',
                icon: <IOut size={25} />,
                color: isDark ? '#f87171' : '#dc2626',
                disabled: !permissions.out || itemState.isActive === false,
              })}
            {permissions.transfer &&
              renderButton({
                onClick: onItemTransfer,
                label: 'Transfer',
                icon: <IOut size={25} />,
                color: '#5970d7',
                disabled: !permissions.transfer || itemState.isActive === false,
              })}
            {permissions.auditItem &&
              renderButton({
                onClick: onItemManage,
                label: 'History',
                icon: <IManage size={25} />,
                color: isDark ? '#e2e8f0' : 'black',
                disabled: !permissions.auditItem || itemState.isActive === false,
              })}
          </MenuList>
        )}
        {!itemState.archived && (
          <>
            <div
              style={{
                fontSize: '11px',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                color: isDark ? '#64748b' : '#94a3b8',
                marginTop: '20px',
                marginBottom: '8px',
                paddingLeft: '4px',
              }}>
              Manage
            </div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: '8px',
                marginBottom: '35px',
              }}>
              {renderManageButton({
                onClick: () => setOpenModifyDialog(true),
                label: 'Modify Quantity',
                icon: <IEdit size={20} />,
                disabled: !permissions.modify,
              })}
              {renderManageButton({
                onClick: () => setOpenAdjustUnitRateDialog(true),
                label: 'Unit Rate',
                icon: <Icons.UnitRate size={20} />,
                disabled: !permissions.modify,
                secondary: itemState.adjustedUnitRate && itemState.adjustedUnitRate.value > 0
                  ? `${Number(itemState.adjustedUnitRate.value).toFixed(2)}`
                  : 'Not set',
                secondaryMuted: !(itemState.adjustedUnitRate && itemState.adjustedUnitRate.value > 0),
              })}
              {renderManageButton({
                onClick: () => {
                  setReorderValue(String(itemState.lowStockThreshold || ''));
                  setOpenReorderDialog(true);
                },
                label: 'Reorder Level',
                icon: <IAlert size={20} />,
                secondary: itemState.lowStockThreshold > 0
                  ? `≤ ${itemState.lowStockThreshold}`
                  : 'Not set',
                secondaryMuted: !(itemState.lowStockThreshold > 0),
              })}
              {(() => {
                const sectionsEnabled = !!(itemState.store && itemState.store.enableSections);
                const hasLocation = !!(itemState.location && (itemState.location.rack || itemState.location.shelf || itemState.location.level));
                return renderManageButton({
                  onClick: () => sectionsEnabled
                    ? setOpenLocationDialog(true)
                    : setOpenSectionsHelp(true),
                  label: 'Location',
                  icon: <ILocation size={20} />,
                  // Not disabled — tapping explains why it's off and how to fix it.
                  mutedIcon: !sectionsEnabled,
                  secondary: !sectionsEnabled
                    ? 'Off for this store'
                    : (hasLocation
                      ? [itemState.location.rack, itemState.location.shelf, itemState.location.level].filter(Boolean).join('-')
                      : 'Not set'),
                  secondaryMuted: !sectionsEnabled || !hasLocation,
                });
              })()}
              {renderManageButton({
                onClick: () => setOpenLabelDialog(true),
                label: 'Generate Label',
                icon: <IQRCode size={20} />,
              })}
              {renderManageButton({
                onClick: () => setOpenEditTypeConfirm(true),
                label: 'Edit Item Type',
                icon: <IBox size={20} />,
              })}
              {renderManageButton({
                onClick: () => setOpenArchiveConfirm(true),
                label: 'Archive Item',
                icon: <IArchive size={20} />,
                disabled: disableArchiving || !permissions.archive,
                danger: true,
                secondary: disableArchiving
                  ? 'Products linked'
                  : undefined,
              })}
            </div>
          </>
        )}
      </DialogContent>
      {openLabelDialog && (
        <Suspense fallback={<div>Loading...</div>}>
          <LabelDialog
            item={itemState}
            open={openLabelDialog}
            onClose={() => setOpenLabelDialog(false)}
          />
        </Suspense>
      )}
      <Dialog
        open={openSectionsHelp}
        onClose={() => setOpenSectionsHelp(false)}
        maxWidth="xs"
        fullWidth>
        <DialogTitle style={{ paddingBottom: '4px' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '16px' }}>
            <ILocation size={20} />
            Locations are off for this store
          </span>
        </DialogTitle>
        <DialogContent>
          <div style={{ fontSize: '13px', lineHeight: 1.6, color: isDark ? '#cbd5e1' : '#334155' }}>
            <p style={{ marginTop: 0 }}>
              Item locations (rack, shelf and level) are turned on per store.
              This store — <strong>{(itemState.store && itemState.store.name) || 'this store'}</strong> —
              has them switched off, so items here can't record a location yet.
            </p>
            <p style={{ margin: '14px 0 4px', fontWeight: 600, color: isDark ? '#e2e8f0' : '#0f172a' }}>
              To turn them on
            </p>
            <p style={{ margin: 0 }}>
              Open this store, choose <strong>Edit Store</strong>, tick{' '}
              <strong>Enable Sections (item locations)</strong>, then{' '}
              <strong>Save Store</strong>.
            </p>
            <p
              style={{
                margin: '16px 0 0',
                paddingTop: '12px',
                borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : '#e8e7e8'}`,
                color: isDark ? '#94a3b8' : '#64748b',
              }}>
              You are signed in as <strong>{currentRole().displayName}</strong>.
              {' '}{supportContactLine()}
            </p>
          </div>
          <div style={{ textAlign: 'right', margin: '18px 0 8px' }}>
            <span
              onClick={() => setOpenSectionsHelp(false)}
              style={{
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: 600,
                color: isDark ? '#818cf8' : '#6366f1',
                padding: '8px 4px',
              }}>
              Got it
            </span>
          </div>
        </DialogContent>
      </Dialog>
      <LocationDialog
        open={openLocationDialog}
        onUpdate={() => {
          if (onModifyStoreItem) {
            onModifyStoreItem();
          }
        }}
        item={itemState}
        onClose={() => setOpenLocationDialog(false)}
      />
      {openAdjustUnitRateDialog && (
        <AdjustUnitRateDialog
          item={itemState}
          open={openAdjustUnitRateDialog}
          onAdjustUnitRate={() => {
            if (onAdjustUnitRate) {
              onAdjustUnitRate();
            }
          }}
          onClose={() => setOpenAdjustUnitRateDialog(false)}
        />
      )}
      <ModifyDialog
        open={openModifyDialog}
        onClose={() => setOpenModifyDialog(false)}
        item={itemState}
        onModifyStoreItem={() => {
          if (onModifyStoreItem) {
            onModifyStoreItem();
          }
        }}
      />
      <AlertDialog
        open={openEditTypeConfirm}
        onClose={() => setOpenEditTypeConfirm(false)}
        onConfirm={() => {
          setOpenEditTypeConfirm(false);
          setOpenEditItemTypeDialog(true);
        }}
        confirmBtn={
          <span style={{ color: isDark ? '#818cf8' : '#4f46e5' }}>
            Continue
          </span>
        }
        description={'Changes to the item type will affect all store items that use this type across all stores.'}
      />
      {openEditItemTypeDialog && (
        <EditItemTypeDialog
          open={openEditItemTypeDialog}
          itemType={itemState.type}
          onClose={() => setOpenEditItemTypeDialog(false)}
          onUpdate={() => {
            if (onModifyStoreItem) {
              onModifyStoreItem();
            }
          }}
        />
      )}
      <Dialog
        open={openReorderDialog}
        onClose={() => setOpenReorderDialog(false)}
        maxWidth="xs"
        fullWidth>
        <DialogTitle>Set Reorder Level</DialogTitle>
        <DialogContent>
          <div style={{ fontSize: '13px', color: isDark ? '#94a3b8' : '#64748b', marginBottom: '12px' }}>
            Items at or below this quantity will show a "Low Stock" alert.
            Set to 0 to disable.
          </div>
          <input
            type="number"
            min="0"
            autoFocus
            value={reorderValue}
            onChange={(e) => setReorderValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleReorderSave();
            }}
            placeholder="e.g. 50"
            style={{
              width: '100%',
              fontSize: '16px',
              padding: '10px 12px',
              border: `1px solid ${isDark ? 'rgba(255,255,255,0.2)' : '#cbd5e1'}`,
              borderRadius: '8px',
              backgroundColor: isDark ? '#2a2a2e' : '#fff',
              color: isDark ? '#e2e8f0' : '#0f172a',
              outline: 'none',
              boxSizing: 'border-box',
            }}
          />
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '16px', marginBottom: '8px' }}>
            <span
              onClick={() => setOpenReorderDialog(false)}
              style={{
                padding: '8px 16px',
                fontSize: '14px',
                color: isDark ? '#94a3b8' : '#64748b',
                cursor: 'pointer',
                borderRadius: '6px',
              }}>
              Cancel
            </span>
            <span
              onClick={handleReorderSave}
              style={{
                padding: '8px 16px',
                fontSize: '14px',
                fontWeight: 600,
                color: '#fff',
                backgroundColor: '#6366f1',
                borderRadius: '6px',
                cursor: 'pointer',
              }}>
              Save
            </span>
          </div>
        </DialogContent>
      </Dialog>
      <AlertDialog
        open={openArchiveConfirm}
        onClose={() => setOpenArchiveConfirm(false)}
        onConfirm={() => {
          setOpenArchiveConfirm(false);
          _onArchiveItem();
        }}
        confirmBtn={
          <span style={{ color: isDark ? '#f87171' : '#dc2626' }}>
            Yes, Archive Item
          </span>
        }
        description={`Are you sure you want to archive "${itemState.type ? itemState.type.name : 'this item'}"? This will remove it from the active store items list.`}
      />
    </Dialog>
  );
}

ActionDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  item: PropTypes.object,
  permissions: PropTypes.object,
  itemProducts: PropTypes.array,
  onItemIn: PropTypes.func,
  onItemOut: PropTypes.func,
  onItemTransfer: PropTypes.func,
  onItemManage: PropTypes.func,
  onItemDelete: PropTypes.func,
  onItemRestore: PropTypes.func,
  onModifyStoreItem: PropTypes.func,
  onArchiveStoreItem: PropTypes.func,
  onAdjustUnitRate: PropTypes.func,
};

export default ActionDialog;
